import { createContext, useState, useEffect, useContext, ReactNode } from 'react';

interface DataSourceContextType {
  useAwsDynamoDB: boolean;
  setUseAwsDynamoDB: (value: boolean) => void;
  isConfigured: boolean;
  awsConfig: {
    region: string;
    tableName: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  };
  updateAwsConfig: (config: {
    region: string;
    tableName: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  }) => void;
}

const defaultAwsConfig = {
  region: 'us-east-1',
  tableName: 'docutrackr-customers',
};

const DataSourceContext = createContext<DataSourceContextType | undefined>(undefined);

export const DataSourceProvider = ({ children }: { children: ReactNode }) => {
  const [useAwsDynamoDB, setUseAwsDynamoDBState] = useState<boolean>(() => {
    const saved = localStorage.getItem('useAwsDynamoDB');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [awsConfig, setAwsConfig] = useState(() => {
    const savedConfig = localStorage.getItem('awsConfig');
    return savedConfig ? JSON.parse(savedConfig) : defaultAwsConfig;
  });

  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    localStorage.setItem('useAwsDynamoDB', JSON.stringify(useAwsDynamoDB));
  }, [useAwsDynamoDB]);

  useEffect(() => {
    localStorage.setItem('awsConfig', JSON.stringify(awsConfig));
    // Check if AWS is configured with at least region and table name
    setIsConfigured(!!awsConfig.region && !!awsConfig.tableName);
  }, [awsConfig]);

  const setUseAwsDynamoDB = (value: boolean) => {
    setUseAwsDynamoDBState(value);
  };

  const updateAwsConfig = (config: {
    region: string;
    tableName: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  }) => {
    setAwsConfig(config);
  };

  return (
    <DataSourceContext.Provider
      value={{
        useAwsDynamoDB,
        setUseAwsDynamoDB,
        isConfigured,
        awsConfig,
        updateAwsConfig,
      }}
    >
      {children}
    </DataSourceContext.Provider>
  );
};

export const useDataSource = (): DataSourceContextType => {
  const context = useContext(DataSourceContext);
  if (context === undefined) {
    throw new Error('useDataSource must be used within a DataSourceProvider');
  }
  return context;
};
